<template>
  <div class="container" :class="contentClass ? contentClass : ''">
    <div
      class="content"
      :class="contentClass ? `${contentClass}--content` : ''"
    >
      <video
        ref="retentionVideo"
        :class="`${viewsText}-text`"
        v-if="!isExternalSourceVideo"
      ></video>
      <apexchart
        :class="{ darkThemeSvgFix: $vuetify.theme.dark }"
        v-if="chartLoaded"
        ref="realtimechart"
        class="retention-chart"
        :height="retentionChartHeight"
        :width="retentionChartWidth"
        type="area"
        :options="retentionChartOptions"
        :series="retentionChartSeries"
      ></apexchart>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { addSeconds, format } from 'date-fns'
import Hls from 'hls.js'
import _ from 'lodash'
export default {
  props: [
    'retentionChartSeries',
    'm3u8Url',
    'isExternalSourceVideo',
    'annotationPoints',
    'contentClass',
  ],
  name: 'RetentionChartVideoV3',
  components: {
    apexchart: VueApexCharts,
  },
  computed: {
    isComparisonRetentionChart() {
      return this.contentClass && this.contentClass === 'video-comparison-chart'
    },
    retentionChartHeight() {
      if (this.isComparisonRetentionChart) return '252px'
      return '346px'
    },
    retentionChartWidth() {
      if (this.isComparisonRetentionChart) return '366px'
      return '100%'
    },
  },
  data: function () {
    return {
      currentTime: 0,
      viewsText: 'x-small',
      retentionChartOptions: {
        legend: {
          show: false,
        },
        annotations: {
          points: [],
        },
        chart: {
          events: {
            mouseLeave: () => this.onChartLeave(),
            mouseMove: (e, ctx, conf) => this.onChartMouseMove(conf),
            mounted: () => this.onChartMounted(),
          },
          toolbar: false,
          type: 'area',
          width: '100%',
          offsetX: 0,
          offsetY: 0,
        },
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          tickAmount: 4,
          type: 'numeric',
          labels: {
            rotate: 0,
            offsetX: 2,
            formatter: (val) => {
              if (val) {
                if (val > 3600)
                  return format(addSeconds(new Date(0), val), 'HH:mm:ss')
                return format(addSeconds(new Date(0), val), 'mm:ss')
              } else {
                return format(new Date(0), 'mm:ss')
              }
            },
          },
          tooltip: {
            enabled: false,
          },
        },
        yaxis: {
          type: 'numeric',
        },
        tooltip: {
          theme: 'dark',
          enabled: true,
          y: {
            formatter: (val) => {
              return `${val} / ${
                ((val * 100) / this.maxValue).toFixed(0) + '%'
              }`
            },
          },
        },
        stroke: { curve: 'straight', width: 2 },
        colors: ['#3F55EE', '#20C1A4'],
      },
      maxValue: 0,
      chartLoaded: false,
      hls: null,
      throttledHover: null,
      lastSegT: null,
      lastLoadTs: 0,
      cancelTimer: null,
      cancelWindowMs: 500,
      lastIdx: -1,
    }
  },
  created() {
    this.throttledHover = _.throttle(this.handleChartHover, 80, {
      leading: true,
      trailing: true,
    })
  },
  mounted() {
    this.initChart()
    if (!this.isExternalSourceVideo) this.initHls()
  },
  beforeDestroy() {
    this.throttledHover?.cancel?.()
    clearTimeout(this.cancelTimer)
    this.hls?.destroy?.()
  },
  methods: {
    initChart() {
      const retentionValues = this.retentionChartSeries[0].data.map(
        (item) => item.y
      )
      const viewsValues = this.retentionChartSeries[1].data.map(
        (item) => item.y
      )
      this.maxValue = Math.max(...retentionValues, ...viewsValues)

      if (this.annotationPoints && this.annotationPoints.length > 0) {
        this.retentionChartOptions.annotations.points = this.annotationPoints
      }

      if (Math.max(...viewsValues) >= 10) {
        this.viewsText = 'small'
      }
      if (Math.max(...viewsValues) >= 100) {
        this.viewsText = 'medium'
      }
      if (Math.max(...viewsValues) >= 1000) {
        this.viewsText = 'big'
      }

      const textColor = this.$vuetify.theme.currentTheme['neutral-40-light']
      const isDark = this.$vuetify.theme.dark
      this.retentionChartOptions.xaxis.labels.style = {
        fontFamily: 'Rubik, sans-serif',
        fontSize: '11px',
        fontWeight: 400,
        colors: textColor,

        padding: {
          left: 0,
          bottom: 0,
          right: 0,
          top: 0,
        },
      }
      this.retentionChartOptions.grid = {
        show: false,
      }
      this.retentionChartOptions.yaxis = [
        {
          max:
            Math.max(this.maxValue) === 0 ? 10 : Math.max(...retentionValues),
          labels: {
            formatter: (val, i) => {
              if (val) {
                return (i * 10).toFixed(0) + '%'
              }
              return '0%'
            },
            style: {
              fontFamily: 'Rubik, sans-serif',
              fontSize: '11px',
              fontWeight: 400,
              colors: textColor,
            },
          },
          show: false,
        },
        {
          opposite: true,
          max: Math.max(this.maxValue) === 0 ? 10 : Math.max(...viewsValues),
          labels: {
            formatter: (val) => {
              if (val) {
                if (val > 1000000) {
                  return `${(val / 1000000).toFixed(1)}M`
                }
                if (val > 1000) {
                  return `${(val / 1000).toFixed(1)}k`
                }
                return val.toFixed(0)
              }
              return '0'
            },
          },
          show: false,
        },
      ]

      this.retentionChartOptions.tooltip = {
        theme: isDark ? 'dark' : 'light',
        enabled: true,
        y: {
          formatter: (val, i) => {
            if (i.seriesIndex === 0) {
              return val === 0 && this.maxValue === 0
                ? '0%'
                : `${((val * 100) / this.maxValue).toFixed(0) + '%'}`
            }
            return val
          },
        },
      }

      this.setComparisonRetentionChart()

      this.chartLoaded = true
    },
    setComparisonRetentionChart() {
      this.retentionChartOptions.xaxis.axisTicks = { show: false }
    },
    initHls() {
      const video = this.$refs.retentionVideo
      if (Hls.isSupported()) {
        const hlsSettings = {
          startLevel: 0,
          testBandwidth: false,
          autoStartLoad: false,
          maxBufferLength: 2,
          maxMaxBufferLength: 2,
          maxBufferSize: 8 * 1024 * 1024,
          backBufferLength: 1,
          lowLatencyMode: false,
          enableWorker: true,
        }
        this.hls = new Hls(hlsSettings)
        this.hls.loadSource(this.m3u8Url)
        this.hls.attachMedia(this.$refs.retentionVideo)
        this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
          this.hls.autoLevelEnabled = false
          this.hls.currentLevel = 0
          this.$refs.retentionVideo.pause()
        })
      } else if (video && video.canPlayType('application/vnd.apple.mpegurl')) {
        this.hls = this.$refs.retentionVideo
        this.hls.src = this.m3u8Url
        this.hls.addEventListener('canplay', () => {
          this.$refs.retentionVideo.pause()
        })
      }
    },
    onChartMounted() {
      this.switchPreviewTo(5, { immediate: true })
    },
    onChartPointChange(conf) {
      this.throttledHover(conf)
    },
    onChartMouseMove(conf) {
      const idx = conf?.dataPointIndex ?? -1
      if (idx !== this.lastIdx) {
        if (this.lastIdx === -1 && idx >= 0) this.onChartEnter(conf)
        else if (this.lastIdx >= 0 && idx === -1) this.onChartLeave()
        else if (idx >= 0) this.onChartPointChange?.(conf)
        this.lastIdx = idx
      }
    },
    onChartEnter(conf) {
      const t = this.getTimeFromHoveredPoint(conf)
      if (t == null) return
      this.switchPreviewTo(t, { immediate: true })
    },
    onChartLeave() {
      clearTimeout(this.cancelTimer)
      this.cancelTimer = null
      this.lastSegT = null
      this.hls?.stopLoad?.()
      this.$refs.retentionVideo.pause?.()
    },

    handleChartHover(conf) {
      const t = this.getTimeFromHoveredPoint(conf)
      if (t == null) return
      this.switchPreviewTo(t)
    },
    getTimeFromHoveredPoint(conf) {
      const { dataPointIndex, seriesIndex = 0, config, globals } = conf
      if (dataPointIndex == null || dataPointIndex < 0) return null
      const xList =
        globals?.seriesX?.[seriesIndex] ||
        config?.series?.[seriesIndex]?.data?.map((p) => p.x) ||
        []
      return xList[dataPointIndex] ?? null
    },
    switchPreviewTo(timeSec, { immediate = false } = {}) {
      const segDur = this.getSegmentDuration()

      const snapped = this.snapToSegment(timeSec, segDur)

      if (snapped === this.lastSegT && !immediate) return

      const now = Date.now()
      const movedWithinWindow = now - this.lastLoadTs < this.cancelWindowMs

      if (movedWithinWindow) {
        this.hls?.stopLoad?.()
      }

      const video = this.$refs.retentionVideo
      if (video) video.currentTime = snapped

      this.hls?.startLoad?.(snapped)
      this.lastLoadTs = now
      this.lastSegT = snapped

      clearTimeout(this.cancelTimer)
      this.cancelTimer = setTimeout(() => {}, this.cancelWindowMs)
    },
    getSegmentDuration() {
      const lvl = this.hls?.levels?.[this.hls?.currentLevel || 0]
      const det = lvl?.details
      return det?.targetduration || det?.fragments?.[0]?.duration || 2
    },
    snapToSegment(t, segDur) {
      return Math.floor(t / segDur) * segDur
    },
  },
}
</script>
<style lang="scss">
.apexcharts-tooltip {
  background-color: varFigma(neutral-100-white) !important;
  color: varFigma(neutral-20-dark) !important;
  border: 1px solid varFigma(neutral-95-lightest) !important;
}

.apexcharts-tooltip-title {
  background-color: varFigma(neutral-95-lightest) !important;
  color: varFigma(neutral-20-dark) !important;
  border-color: varFigma(neutral-95-lightest) !important;
  font-weight: 500;
  padding: 6px 10px;
}
</style>
<style lang="scss" scoped>
.container {
  &:not(.video-comparison-chart) {
    width: 100%;
    height: 400px;
    position: relative;
  }
  &:has(.video-comparison-chart--content) {
    transform: translate(24px, 0px);
    padding: 0 !important;
    margin: 0 !important;
    height: 204px;
  }
}
.pitch-retention {
  .video-comparison-chart {
    height: 252px !important;
  }
}
.content {
  &:not(.video-comparison-chart--content) {
    position: absolute;
    top: 0;
    left: 0;
    width: calc(100% + 30px);
    height: 100%;
    margin-top: -16px;
    .retention-chart {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      margin-top: -11px;
    }
  }
}
.video-comparison-chart--content {
  position: relative;
  pointer-events: none;
  .retention-chart::v-deep {
    .apexcharts-canvas {
      pointer-events: auto;
      transform: translate(-12px, -20px);
      svg {
        .apexcharts-xaxis {
          display: none;
        }
      }
    }
  }
}
.container:not(.video-comparison-chart) {
  video,
  .x-small-text,
  .small-text,
  .medium-text,
  .big-text {
    position: absolute;
    background-color: #000;
    width: calc(100% - 36px);
    top: 18px;
    left: 12px;
    height: 280px;
  }
  &:has(.video-comparison-chart--content) {
    video,
    .x-small-text,
    .small-text,
    .medium-text,
    .big-text {
      top: 0;
      left: 0;
      height: 204px;
      width: 328px;
    }
  }
}
</style>
